<template>
  <div class="content">
    <h1 class="title">{{ $t("ticketBuyers.title") }}</h1>
    <div class="table">
      <div class="inputs">
        <v-row>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6" v-if="isSuperAdmin">
            <v-text-field
              :label="$t('ticketBuyers.name')"
              v-model="name"
              @keydown.enter="handleFilter(false)"
            ></v-text-field>
          </v-col>
          <v-col cols="12" xs="12" lg="3" md="4" sm="6">
            <v-text-field
              :label="$t('ticketBuyers.email')"
              v-model="email"
              @keydown.enter="handleFilter(false)"
              type="email"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-btn @click="handleFilter(false)">{{
          $t("capacity.filterButton")
        }}</v-btn>
      </div>

      <div>
        <v-data-table
          v-if="!isLoading"
          :headers="headers"
          :items="ticketBuyersEvents"
          disable-sort
          hide-default-footer
          :items-per-page="-1"
          class="elevation-0"
        >
          <template v-slot:headers>
            <tr>
              <th
                v-for="header in headers"
                :key="header.key"
                :style="{
                  'text-align': header.key === 'icon' ? 'center' : 'left',
                }"
                class="header-text"
              >
                {{ !this.isSuperAdmin ? header.title : header.titleSa }}
              </th>
            </tr>
          </template>
          <template v-slot:[`item.icon`]="{ item }" v-if="!this.isSuperAdmin">
            <v-btn
              @click="handleSelectTypeButton(item.selectable)"
              icon="mdi-pencil"
              variant="text"
            ></v-btn>
          </template>
          <template v-slot:no-data>
            <Vue3Lottie
              :animationData="NoDataLottie"
              :height="200"
              :width="200"
            />
            <div class="pb-5 table-no-data">
              <h3>{{ $t("tableNoDataTitle") }}</h3>
              <p>{{ $t("tableNoDataDesc") }}</p>
            </div>
          </template>
          <template v-slot:bottom>
            <div class="botSide">
              <v-row align="center" justify-sm="end" justify="center" class="px-2">
                <v-col cols="12" xs="12" lg="3" md="4" sm="6">
                  <v-row align="center" justify="end"
                    ><div class="pr-2 pageSelectorLabel">
                      {{ $t("anomaly.tableFooterText") }}
                    </div>
                    <v-select
                      v-model="limit"
                      hide-details
                      @update:model-value="handleFilter(false)"
                      :items="pageSizes"
                      class="pageSelector pl-5"
                    ></v-select>
                    <div class="pl-2">
                      {{ displayRange }}
                    </div>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  xs="12"
                  lg="3"
                  md="4"
                  sm="5"
                  class="pl-1 pr-1 pagination"
                >
                  <v-pagination
                    v-model="page"
                    :length="pageCount"
                    rounded="circle"
                    :total-visible="1"
                    show-first-last-page="true"
                    @update:model-value="handleFilter(true)"
                  ></v-pagination>
                </v-col>
              </v-row>
            </div>
          </template>
        </v-data-table>
      </div>
      <v-card v-if="isLoading" class="loading">
        <v-progress-circular
          v-if="isLoading"
          size="73.5"
          indeterminate
          class="loading-circle"
        ></v-progress-circular>
      </v-card>
    </div>
  </div>
</template>

<script>
import { Vue3Lottie } from "vue3-lottie";
import NoDataLottie from "@/assets/lotties/nodatalottie.json";
import { mapActions, mapGetters } from "vuex";
import TicketBuyerTypesModal from "@/components/global/modalComponents/ticketBuyerTypesModal/TicketBuyerTypesModal.vue";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "TicketBuyers",
  props: {},
  components: {
    Vue3Lottie,
  },
  data() {
    return {
      NoDataLottie,
      headers: [
        {
          title: this.$t("ticketBuyers.table.headers.full_name"),
          titleSa: this.$t("ticketBuyers.table.headers.full_name"),
          key: "full_name",
          align: "start",
          sortable: false,
        },
        {
          title: this.$t("ticketBuyers.table.headers.email"),
          titleSa: this.$t("ticketBuyers.table.headers.email"),
          key: "email",
          sortable: false,
        },
        {
          title: this.$t("ticketBuyers.table.headers.tax_number"),
          titleSa: this.$t("ticketBuyers.table.headers.tax_number"),
          key: "tax_number",
          sortable: false,
        },
        {
          title: this.$t("ticketBuyers.table.headers.type"),
          key: "types[0].title",
          sortable: false,
        },
        {
          title: this.$t("ticketBuyers.table.headers.icon"),
          key: "icon",
          align: "center",
          sortable: false,
        },
      ],

      //Pagination settings
      page: undefined,
      limit: undefined,
      total: 0,
      pageSizes: [10, 25, 50, 100],

      //For Search
      name: undefined,
      email: undefined,

      ticketBuyersEvents: [],

      isLoading: false,
      error: false,
    };
  },
  created() {
    this.getTicketBuyersMethod();
  },

  methods: {
    ...mapActions({
      getTicketBuyers: "users/getTicketBuyers",
      addTicketBuyerTypeToTicketBuyer: "users/addTicketBuyerTypeToTicketBuyer",
    }),
    getTicketBuyersMethod(params) {
      this.isLoading = true;
      this.getTicketBuyers(params)
        .then((res) => {
          console.log(params);
          this.isLoading = false;
          this.ticketBuyersEvents = res.list;
          this.page = res.page;
          this.limit = res.limit;
          this.total = res.total;
        })
        .catch((err) => {
          console.log(err);
          this.isLoading = false;
          this.error = true;
        });
    },
    handleFilter(hasPage) {
      const params = {
        email: this.email !== undefined ? this.email : null,
        name: this.name !== undefined ? this.name : null,
        limit: this.limit,
        page: hasPage ? this.page : 1,
      };
      this.getTicketBuyersMethod(params);
    },
    handleSelectTypeButton(ticketBuyer) {
      this.emitter.emit("showComponentModalModal", {
        bind: {
          is: TicketBuyerTypesModal,
          id: ticketBuyer.id,
        },
        on: {
          selectedType: (createPayload) => {
            console.log(createPayload);
            this.addTicketBuyerTypeToTicketBuyer(createPayload)
              .then(() => {
                toast.success(this.$t("ticketBuyers.succesMessage"));
                this.getTicketBuyersMethod();
              })
              .catch((err) => {
                console.log(err);
              });
          },
        },
      });
    },
  },
  computed: {
    ...mapGetters({ loggedInUser: "users/loggedInUser" }),
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    displayRange() {
      const start = (this.page - 1) * this.limit + 1;
      const end = Math.min(start + this.limit - 1, this.total);
      return `${start}-${end} of ${this.total}`;
    },
    isSuperAdmin() {
      return this.loggedInUser && this.loggedInUser.role === 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.header-text {
  font-weight: 600 !important;
  font-size: 14px !important;
  color: #000000de !important;
}
</style>
